import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Skull, ArrowUp, ArrowDown, X, Loader } from "lucide-react";

/**
 * Rivals panel — small card showing your marked rivals with horde-deltas.
 * Lives on Command page. Up to 5 rivals (MAX_RIVALS on backend).
 *
 * Refreshes when battleTick changes (after each battle/event resolution).
 */
export default function RivalsPanel() {
  const { auth, battleTick } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const load = useCallback(async () => {
    if (!auth) return;
    try {
      const { data } = await api.get("/rivals/list");
      setData(data);
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => { load(); }, [load, battleTick]);

  // On mount, surface rivalry notifications as toasts then ack
  useEffect(() => {
    if (!auth) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/rivals/notifications");
        if (cancelled || !data?.notifications?.length) return;
        for (const n of data.notifications.slice(0, 5)) {
          const { toast } = await import("sonner");
          if (n.type === "promoted") toast(n.message, { icon: "↑" });
          else if (n.type === "overtake") toast.error(n.message);
          else toast(n.message, { icon: "+" });
        }
        // Ack a moment later so user sees the toasts
        setTimeout(() => { api.post("/rivals/ack").catch(() => {}); }, 4000);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [auth]);

  // Refresh when the leaderboard MARK/UNMARK button toggles a rival
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("rivals-changed", handler);
    return () => window.removeEventListener("rivals-changed", handler);
  }, [load]);

  const unmark = async (uid) => {
    setActing(true);
    try {
      await api.post("/rivals/unmark", { user_id: uid });
      await load();
    } finally {
      setActing(false);
    }
  };

  if (loading || !data) return null;
  if (!data.rivals || data.rivals.length === 0) {
    return (
      <div className="tactical-card" data-testid="rivals-panel-empty">
        <p className="tac-label flex items-center gap-2">
          <Skull className="w-3 h-3 text-blood" /> RIVALS
        </p>
        <p className="text-[0.65rem] text-muted-h mt-2 leading-relaxed">
          Mark up to {data.max} opponents from the leaderboard. Their fate
          will follow yours.
        </p>
      </div>
    );
  }

  return (
    <div className="tactical-card" data-testid="rivals-panel">
      <p className="tac-label flex items-center gap-2 mb-3">
        <Skull className="w-3 h-3 text-blood" /> RIVALS // {data.rivals.length} OF {data.max}
      </p>
      <ul className="space-y-2" data-testid="rivals-list">
        {data.rivals.map((r) => {
          const ahead = r.delta_vs_me > 0;    // I'm ahead
          const behind = r.delta_vs_me < 0;
          return (
            <li key={r.user_id}
                className="flex items-center gap-3"
                data-testid={`rival-row-${r.user_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-bone truncate">{r.callsign}</p>
                <p className="font-mono text-[0.6rem] text-muted-h tracking-wider">
                  {r.rank_name?.toUpperCase()} · {r.horde.toLocaleString()} HORDE
                </p>
              </div>
              <span className={`font-mono text-[0.7rem] tabular-nums inline-flex items-center gap-1 ${
                ahead ? "text-[#7FBA9D]" : behind ? "text-blood" : "text-muted-h"
              }`}
              data-testid={`rival-delta-${r.user_id}`}>
                {ahead && <ArrowUp className="w-2.5 h-2.5" />}
                {behind && <ArrowDown className="w-2.5 h-2.5" />}
                {ahead ? "+" : behind ? "" : "="}{Math.abs(r.delta_vs_me).toLocaleString()}
              </span>
              <button onClick={() => unmark(r.user_id)}
                      disabled={acting}
                      className="text-muted-h hover:text-blood transition-colors p-1 -m-1"
                      aria-label="Unmark rival"
                      data-testid={`rival-unmark-${r.user_id}`}>
                {acting ? <Loader className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
